import React, { useState } from "react";
import styled from "@emotion/styled";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import {
  spacing,
  fontSize,
  fontFamily,
  fontWeight,
  borderRadius,
  border as borderToken,
  surface,
  text,
} from "@atlas-int/tokens";

// ─── Shared styled helpers ───────────────────────────────────────────────────

const SubSection = styled.div`
  margin-bottom: ${spacing[10]};
`;

const SubTitle = styled.h3`
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[2]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.sm};
  color: ${text.secondary};
  margin: 0 0 ${spacing[4]} 0;
`;

const DemoArea = styled.div`
  padding: ${spacing[6]};
  border: 1px dashed ${borderToken.default};
  border-radius: ${borderRadius.base};
  margin-bottom: ${spacing[4]};
`;

const PlaceholderTag = styled.span`
  display: inline-block;
  font-family: ${fontFamily.mono};
  font-size: ${fontSize.xs};
  color: ${text.muted};
  background-color: ${surface.muted};
  padding: ${spacing[1]} ${spacing[3]};
  border-radius: ${borderRadius.base};
  margin-bottom: ${spacing[4]};
`;

// ─── NavModalTrigger placeholder ─────────────────────────────────────────────

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  background-color: ${surface.white};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  color: ${text.primary};
  cursor: pointer;

  &:hover {
    background-color: ${surface.muted};
  }
`;

const TriggerIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  font-size: 14px;
  line-height: 1;
`;

// ─── SettingsRow placeholder ─────────────────────────────────────────────────

const SettingsRowContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[4]} ${spacing[6]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  background-color: ${surface.white};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const SettingsLabel = styled.span`
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
`;

const SettingsControl = styled.div`
  display: flex;
  align-items: center;
`;

const MockSelect = styled.select`
  padding: ${spacing[1]} ${spacing[4]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  background-color: ${surface.white};
`;

// ─── DisabledOverlay placeholder ─────────────────────────────────────────────

const OverlayWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const OverlayMask = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: ${borderRadius.base};
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TooltipBubble = styled.div`
  background-color: ${text.primary};
  color: ${surface.white};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.xs};
  padding: ${spacing[2]} ${spacing[4]};
  border-radius: ${borderRadius.base};
  white-space: nowrap;
`;

const DisabledContent = styled.div`
  padding: ${spacing[6]};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  background-color: ${surface.white};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
`;

// ─── ColorDot placeholder ────────────────────────────────────────────────────

const Dot = styled.span<{ $color: string; $size?: number }>`
  display: inline-block;
  width: ${({ $size }) => $size ?? 12}px;
  height: ${({ $size }) => $size ?? 12}px;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const DotRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  flex-wrap: wrap;
`;

const DotLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  font-family: ${fontFamily.base};
  font-size: ${fontSize.sm};
  color: ${text.primary};
`;

// ─── Component ───────────────────────────────────────────────────────────────

const UtilityComponentsSection: React.FC = () => {
  const [overlayHovered, setOverlayHovered] = useState(false);

  return (
    <PreviewSection
      id="utility-components"
      title="Utility Components"
      description="Small, composable UI primitives used across the Atlas interface. These components are being developed by the team and will be integrated as they land."
    >
      {/* NavModalTrigger */}
      <SubSection>
        <SubTitle>NavModalTrigger</SubTitle>
        <PlaceholderTag>Upcoming</PlaceholderTag>
        <Description>
          A compact button combining an icon and label that opens an associated modal.
          Used in navigation bars and control panels for actions like sharing, downloading,
          and viewing data notes.
        </Description>
        <DemoArea>
          <div style={{ display: "flex", gap: spacing[4] }}>
            <TriggerButton>
              <TriggerIcon>{"\u2193"}</TriggerIcon>
              Download
            </TriggerButton>
            <TriggerButton>
              <TriggerIcon>{"\u2197"}</TriggerIcon>
              Share
            </TriggerButton>
            <TriggerButton>
              <TriggerIcon>{"\u2139"}</TriggerIcon>
              Data Notes
            </TriggerButton>
          </div>
        </DemoArea>
        <CodeBlock
          code={`import { NavModalTrigger } from "@atlas-int/ui";

<NavModalTrigger
  icon="download"
  label="Download"
  onClick={openDownloadModal}
/>`}
        />
      </SubSection>

      {/* SettingsRow */}
      <SubSection>
        <SubTitle>SettingsRow</SubTitle>
        <PlaceholderTag>Upcoming</PlaceholderTag>
        <Description>
          A form row with a label on the left and a control on the right. Supports an
          optional disabled state that grays out the entire row. Used inside settings
          panels and control drawers.
        </Description>
        <DemoArea>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <SettingsRowContainer>
              <SettingsLabel>Product classification</SettingsLabel>
              <SettingsControl>
                <MockSelect defaultValue="hs92">
                  <option value="hs92">HS92</option>
                  <option value="sitc">SITC</option>
                </MockSelect>
              </SettingsControl>
            </SettingsRowContainer>
            <SettingsRowContainer $disabled>
              <SettingsLabel>Start year (disabled)</SettingsLabel>
              <SettingsControl>
                <MockSelect defaultValue="2020" disabled>
                  <option value="2020">2020</option>
                </MockSelect>
              </SettingsControl>
            </SettingsRowContainer>
          </div>
        </DemoArea>
        <CodeBlock
          code={`import { SettingsRow } from "@atlas-int/ui";

<SettingsRow label="Product classification" disabled={false}>
  <SelectBox value="hs92" options={classificationOptions} />
</SettingsRow>

<SettingsRow label="Start year" disabled>
  <SelectBox value="2020" options={yearOptions} />
</SettingsRow>`}
        />
      </SubSection>

      {/* DisabledOverlay */}
      <SubSection>
        <SubTitle>DisabledOverlay</SubTitle>
        <PlaceholderTag>Upcoming</PlaceholderTag>
        <Description>
          A wrapper that overlays its children with a semi-transparent mask and shows a
          tooltip explaining why the control is unavailable. Used to disable interactive
          controls while still communicating context.
        </Description>
        <DemoArea>
          <OverlayWrapper
            onMouseEnter={() => setOverlayHovered(true)}
            onMouseLeave={() => setOverlayHovered(false)}
          >
            <DisabledContent>
              This control is disabled
            </DisabledContent>
            <OverlayMask>
              {overlayHovered && (
                <TooltipBubble>Not available for services data</TooltipBubble>
              )}
            </OverlayMask>
          </OverlayWrapper>
        </DemoArea>
        <CodeBlock
          code={`import { DisabledOverlay } from "@atlas-int/ui";

<DisabledOverlay
  disabled={isServicesMode}
  tooltip="Not available for services data"
>
  <ProductDropdown />
</DisabledOverlay>`}
        />
      </SubSection>

      {/* ColorDot */}
      <SubSection>
        <SubTitle>ColorDot</SubTitle>
        <PlaceholderTag>Upcoming</PlaceholderTag>
        <Description>
          A small colored circle used in legend rows and tooltip entries to indicate
          category or data-series color. Accepts a color string and optional size.
        </Description>
        <DemoArea>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
            <DotRow>
              <DotLabel>
                <Dot $color="#4A90D9" /> Machinery
              </DotLabel>
              <DotLabel>
                <Dot $color="#7BC47F" /> Agriculture
              </DotLabel>
              <DotLabel>
                <Dot $color="#E8A838" /> Minerals
              </DotLabel>
              <DotLabel>
                <Dot $color="#D94A4A" /> Textiles
              </DotLabel>
            </DotRow>
            <DotRow>
              <DotLabel>
                <Dot $color="#4A90D9" $size={8} /> Small (8px)
              </DotLabel>
              <DotLabel>
                <Dot $color="#7BC47F" $size={12} /> Default (12px)
              </DotLabel>
              <DotLabel>
                <Dot $color="#E8A838" $size={16} /> Large (16px)
              </DotLabel>
            </DotRow>
          </div>
        </DemoArea>
        <CodeBlock
          code={`import { ColorDot } from "@atlas-int/ui";

// In a legend row
<ColorDot color="#4A90D9" />

// With custom size
<ColorDot color="#7BC47F" size={16} />

// In a tooltip entry
<TooltipRow>
  <ColorDot color={category.color} />
  <span>{category.name}</span>
  <span>{category.value}</span>
</TooltipRow>`}
        />
      </SubSection>
    </PreviewSection>
  );
};

export default UtilityComponentsSection;
